import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, X, Search, ChevronUp, ChevronDown, GripVertical, Minus } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';

export default function EditChecklistPage() {
  const navigate = useNavigate();
  const { checklistId } = useParams();
  const brandDropdownRef = useRef(null);
  const [checklist, setChecklist] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('skincare');
  const [productType, setProductType] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brandsList, setBrandsList] = useState([]);
  const [typesList, setTypesList] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [searchedBrands, setSearchedBrands] = useState([]);
  // Keep original product details so products from other categories/brands still render
  const [originalProductDetails, setOriginalProductDetails] = useState([]);

  useEffect(() => {
    fetchChecklist();
    const handleClickOutside = (e) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(e.target)) {
        setShowBrandDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [checklistId]);

  useEffect(() => {
    fetchProducts();
    fetchBrandsAndTypes();
  }, [category]);

  useEffect(() => {
    fetchProducts();
  }, [brandFilter, productType, priceRange]);

  const fetchChecklist = async () => {
    try {
      const response = await authFetch(`${API}/checklists/${checklistId}`);
      if (response.ok) {
        const data = await response.json();
        setChecklist(data);
        setName(data.name);
        setCategory(data.filters?.category || 'skincare');
        setSelectedProducts(data.products || []);
        // Cache product details so cross-category products still render
        setOriginalProductDetails(data.product_details || []);
      } else {
        toast.error('Checklist not found');
        navigate('/checklists');
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
      toast.error('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandsAndTypes = async () => {
    try {
      const [brandsRes, typesRes] = await Promise.all([
        authFetch(`${API}/products/brands-for-category?category=${category}`),
        authFetch(`${API}/products/types?category=${category}`)
      ]);
      if (brandsRes.ok) setBrandsList(await brandsRes.json());
      if (typesRes.ok) setTypesList(await typesRes.json());
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const handleBrandSearch = async (query) => {
    setBrandSearch(query);
    if (query.length >= 1) {
      try {
        const res = await authFetch(`${API}/products/brands-for-category?category=${category}&search=${encodeURIComponent(query)}`);
        if (res.ok) setSearchedBrands(await res.json());
      } catch (e) { /* ignore */ }
    } else {
      setSearchedBrands([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (brandFilter) params.append('brand', brandFilter);
      if (productType) params.append('product_type', productType);
      if (priceRange) {
        const [min, max] = priceRange.split('-');
        if (min) params.append('price_min', min);
        if (max) params.append('price_max', max);
      }

      const response = await authFetch(`${API}/products?${params}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const productTypeNames = {
    'cleanser': 'Cleansers', 'toner': 'Toners', 'serum': 'Serums',
    'moisturizer': 'Moisturizers', 'sunscreen': 'Sunscreens', 'exfoliator': 'Exfoliators',
    'face_mask': 'Face Masks', 'eye_cream': 'Eye Creams', 'lip_balm': 'Lip Balms',
    'body_lotion': 'Body Lotion', 'body_wash': 'Body Wash', 'skin_treatment': 'Skin Treatments',
    'lipstick': 'Lipsticks', 'lip_gloss': 'Lip Gloss', 'lip_liner': 'Lip Liners',
    'foundation': 'Foundations', 'concealer': 'Concealers', 'primer': 'Primers',
    'blush': 'Blush', 'highlighter': 'Highlighters', 'bronzer': 'Bronzers',
    'eyeshadow': 'Eyeshadows', 'eyeliner': 'Eyeliners', 'eyebrow_pencil': 'Eyebrow Pencils',
    'kajal': 'Kajal', 'mascara': 'Mascaras', 'compact': 'Compact Powder',
    'setting_spray': 'Setting Sprays', 'nail_polish': 'Nail Polish',
    'shampoo': 'Shampoos', 'conditioner': 'Conditioners', 'hair_oil': 'Hair Oils',
    'hair_serum': 'Hair Serums', 'hair_treatment': 'Hair Treatments',
    'hair_mask': 'Hair Masks', 'hair_color': 'Hair Color', 'dry_shampoo': 'Dry Shampoo',
    'derma_product': 'Derma Products', 'pharma_skincare': 'Pharma Skincare',
    'pharma_haircare': 'Pharma Haircare', 'supplement': 'Supplements',
  };

  const filteredProducts = availableProducts.filter(product => {
    if (!searchQuery) return true;
    return product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const toggleProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else if (selectedProducts.length < 7) {
      setSelectedProducts([...selectedProducts, productId]);
    } else {
      toast.error('Maximum 7 products allowed');
    }
  };

  const moveProduct = (index, direction) => {
    setSelectedProducts(prev => {
      const arr = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= arr.length) return arr;
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr;
    });
  };

  const getProductById = (productId) =>
    availableProducts.find(p => p.product_id === productId) ||
    originalProductDetails.find(p => p.product_id === productId);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Please enter a checklist name');
      return;
    }

    if (selectedProducts.length < 3) {
      toast.error('Please select at least 3 products');
      return;
    }

    if (selectedProducts.length > 7) {
      toast.error('Maximum 7 products allowed');
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`${API}/checklists/${checklistId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          filters: { category },
          products: selectedProducts
        })
      });

      if (response.ok) {
        toast.success('Checklist updated! Changes will reflect on all your posts.');
        // Notify feed/post cards to refetch this checklist immediately
        window.dispatchEvent(new CustomEvent('checklist:updated', { detail: { checklistId } }));
        navigate('/checklists');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update checklist');
      }
    } catch (error) {
      console.error('Error updating checklist:', error);
      toast.error(error.message || 'Failed to update checklist');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Checklist</h1>
          <button
            data-testid="save-checklist-btn"
            onClick={handleSubmit}
            disabled={saving || selectedProducts.length < 3}
            className="btn-primary px-6 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">Checklist Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Morning Skincare Routine"
              className="w-full px-4 py-3 rounded-xl border-2 border-border bg-white/50 focus:border-primary outline-none placeholder:text-muted-foreground/40 placeholder:italic"
              required
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">Category</label>
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setProductType('');
                setBrandFilter('');
                setPriceRange('');
              }}
              className="w-full px-4 py-3 rounded-xl border-2 border-border bg-white/50 focus:border-primary outline-none"
            >
              <option value="skincare">Face Care</option>
              <option value="makeup">Make Up</option>
              <option value="haircare">Hair Care</option>
              <option value="pharmacy">Pharma</option>
            </select>
          </div>

          {/* 3 Dropdown Filters */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1 relative" ref={brandDropdownRef}>
              <label className="block text-xs font-medium text-muted-foreground">Brand</label>
              <div className="relative">
                <input
                  data-testid="edit-brand-filter"
                  type="text"
                  value={brandFilter || brandSearch}
                  onChange={(e) => {
                    if (brandFilter) setBrandFilter('');
                    handleBrandSearch(e.target.value);
                    setShowBrandDropdown(true);
                  }}
                  onFocus={() => setShowBrandDropdown(true)}
                  placeholder={brandFilter || "Search brand..."}
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-border bg-white/50 focus:border-primary transition-all outline-none text-sm"
                />
                {brandFilter && (
                  <button
                    onClick={() => { setBrandFilter(''); setBrandSearch(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                  >
                    &times;
                  </button>
                )}
              </div>
              {showBrandDropdown && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  <button
                    onClick={() => { setBrandFilter(''); setBrandSearch(''); setShowBrandDropdown(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-muted-foreground"
                  >
                    All Brands
                  </button>
                  {brandSearch.length >= 1 ? (
                    searchedBrands.length > 0 ? searchedBrands.map(b => (
                      <button
                        key={b.brand}
                        onClick={() => { setBrandFilter(b.brand); setBrandSearch(''); setShowBrandDropdown(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-primary/5 flex justify-between items-center"
                      >
                        <span className="truncate">{b.brand}</span>
                        <span className="text-xs text-muted-foreground shrink-0 ml-1">{b.count}</span>
                      </button>
                    )) : (
                      <p className="px-3 py-2 text-xs text-muted-foreground">No brands found</p>
                    )
                  ) : (
                    <p className="px-3 py-2 text-xs text-muted-foreground">Type to search brands...</p>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-muted-foreground">Type</label>
              <select
                data-testid="edit-type-filter"
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border-2 border-border bg-white/50 focus:border-primary transition-all outline-none text-sm"
              >
                <option value="">All Types</option>
                {typesList.map(t => (
                  <option key={t} value={t}>{productTypeNames[t] || t.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-muted-foreground">Price</label>
              <select
                data-testid="edit-price-filter"
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border-2 border-border bg-white/50 focus:border-primary transition-all outline-none text-sm"
              >
                <option value="">Any Price</option>
                <option value="0-300">Under ₹300</option>
                <option value="300-500">₹300 - ₹500</option>
                <option value="500-1000">₹500 - ₹1000</option>
                <option value="1000-2000">₹1000 - ₹2000</option>
                <option value="2000-5000">₹2000 - ₹5000</option>
                <option value="5000-">₹5000+</option>
              </select>
            </div>
          </div>

          {/* Active filter chips */}
          {(brandFilter || productType || priceRange) && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Active:</span>
              {brandFilter && (
                <button onClick={() => setBrandFilter('')} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors">
                  {brandFilter} ×
                </button>
              )}
              {productType && (
                <button onClick={() => setProductType('')} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors">
                  {productTypeNames[productType] || productType} ×
                </button>
              )}
              {priceRange && (
                <button onClick={() => setPriceRange('')} className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full hover:bg-primary/20 transition-colors">
                  ₹{priceRange.replace('-', ' - ₹').replace('- ₹', priceRange.endsWith('-') ? '+' : ' - ₹')} ×
                </button>
              )}
              <button onClick={() => { setBrandFilter(''); setProductType(''); setPriceRange(''); }} className="text-xs text-red-500 hover:underline ml-1">
                Clear all
              </button>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-border bg-white/50 focus:border-primary outline-none placeholder:text-muted-foreground/40 placeholder:italic"
            />
          </div>

          {/* Selected Products - Reorder */}
          {selectedProducts.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Your Order ({selectedProducts.length}/7)
                </h3>
                <span className="text-xs text-muted-foreground">Use arrows to reorder</span>
              </div>
              <div className="space-y-2 p-3 bg-primary/5 rounded-xl border-2 border-primary/20">
                {selectedProducts.map((productId, index) => {
                  const product = getProductById(productId);
                  if (!product) return (
                    <div key={productId} className="flex items-center gap-2 bg-white rounded-lg p-2.5 shadow-sm">
                      <span className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center shrink-0">{index + 1}</span>
                      <span className="text-sm text-muted-foreground flex-1">Loading...</span>
                    </div>
                  );
                  return (
                    <div key={productId} className="flex items-center gap-2 bg-white rounded-lg p-2.5 shadow-sm" data-testid={`selected-product-${index}`}>
                      <GripVertical className="w-4 h-4 text-muted-foreground/40 shrink-0" />
                      <span className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center shrink-0">{index + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{product.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{product.brand}</p>
                      </div>
                      <div className="flex flex-col gap-0.5 shrink-0">
                        <button
                          type="button"
                          data-testid={`edit-move-up-${index}`}
                          onClick={() => moveProduct(index, -1)}
                          disabled={index === 0}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-20 transition-colors"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          data-testid={`edit-move-down-${index}`}
                          onClick={() => moveProduct(index, 1)}
                          disabled={index === selectedProducts.length - 1}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-20 transition-colors"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        type="button"
                        data-testid={`edit-remove-product-${index}`}
                        onClick={() => toggleProduct(productId)}
                        className="p-1.5 rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors shrink-0"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {selectedProducts.length === 0 && (
            <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
              <h3 className="font-bold mb-2">Selected Products (0/7)</h3>
              <p className="text-sm text-muted-foreground">Minimum 3, Maximum 7 products</p>
            </div>
          )}

          {/* Available Products */}
          <div className="space-y-3">
            <h3 className="font-bold">Available Products ({filteredProducts.length})</h3>
            <div className="grid grid-cols-1 gap-3 max-h-[400px] overflow-y-auto">
              {filteredProducts.map(product => (
                <button
                  key={product.product_id}
                  type="button"
                  onClick={() => toggleProduct(product.product_id)}
                  className={`flex items-center gap-4 p-3 rounded-xl border transition-all text-left ${
                    selectedProducts.includes(product.product_id)
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-white hover:border-primary/50'
                  }`}
                >
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="w-12 h-12 rounded-lg object-cover" 
                      onError={(e) => { 
                        e.target.style.display = 'none'; 
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }} 
                    />
                  ) : null}
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br from-pink-400 to-orange-300 items-center justify-center text-white font-bold text-sm shrink-0`} style={{ fontFamily: 'Outfit, sans-serif', display: product.image ? 'none' : 'flex' }}>
                    g
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{product.name}</p>
                    <p className="text-xs text-muted-foreground">{product.brand} • {product.price}</p>
                  </div>
                  {selectedProducts.includes(product.product_id) && (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
